import { AnimatePresence, motion, useInView } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, CheckCheck, CircleAlert, CircleCheck } from "lucide-react";
import { FEES } from "@/data/fees";
import { CountUp, EASE_EXPO, KineticHeading, Magnetic, SPRING_POP, TiltCard } from "@/components/motion/primitives";

const INK = "#0A0F0E";
const BRAND_DARK = "#00332F";
const TEAL = "#2EE6D6";
const TEAL_DEEP = "#00786F";
const TEAL_SOFT = "#CFF9F4";
const SECONDARY = "#46504D";
const PANEL = "#F2F2ED";
const RULE = "#E0E3DE";
const SIGNAL = "#BF4B22";
const SIGNAL_SOFT = "#FBEDE6";

const eur = (n: number) =>
  `€${n.toLocaleString("en-GB", { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;

/* ────────────────────────────────────────────────────────────────────────────
   Card visuals: small pieces of product, the way Wise shows a balance or a
   card instead of an illustration. HTML, so they stay sharp and pass the CSP.
   Every number inside them comes from FEES. Each one plays once, the first
   time its card scrolls into view.
   ──────────────────────────────────────────────────────────────────────────── */

/** Documents checked in turn: most look good, one has something to fix, as in real life. */
function DocsVisual({ play }: { play: boolean }) {
  const rows = [
    { name: "Passport", ok: true },
    { name: "Admission letter", ok: true },
    { name: "Bank statement", ok: false },
    { name: "Health insurance", ok: true },
  ];
  return (
    <div
      className="w-full max-w-[250px] rounded-[20px] bg-white p-4 text-left shadow-[0_1px_2px_rgba(10,15,14,0.06),0_10px_30px_rgba(10,15,14,0.08)] [transform:translateZ(30px)]"
      aria-hidden="true"
    >
      <div className="flex items-baseline justify-between">
        <span className="text-[14px] font-extrabold tracking-[-0.01em]" style={{ color: INK }}>
          Your documents
        </span>
        <span className="text-[12px] font-bold" style={{ color: TEAL_DEEP }}>
          4 of 8
        </span>
      </div>
      <ul className="mt-3 space-y-2">
        {rows.map((r, i) => (
          <motion.li
            key={r.name}
            className="flex items-center justify-between gap-2 text-[13.5px] font-medium"
            style={{ color: INK }}
            initial={{ opacity: 0, x: -10 }}
            animate={play ? { opacity: 1, x: 0 } : undefined}
            transition={{ duration: 0.5, delay: 0.35 + i * 0.18, ease: EASE_EXPO }}
          >
            {r.name}
            <motion.span
              className="flex items-center gap-1 rounded-full px-2 py-[2px] text-[11px] font-bold"
              style={r.ok ? { backgroundColor: TEAL_SOFT, color: BRAND_DARK } : { backgroundColor: SIGNAL_SOFT, color: SIGNAL }}
              initial={{ scale: 0, opacity: 0 }}
              animate={play ? { scale: 1, opacity: 1 } : undefined}
              transition={{ ...SPRING_POP, delay: 0.6 + i * 0.18 }}
            >
              {r.ok ? <Check className="h-3 w-3" strokeWidth={3} /> : <CircleAlert className="h-3 w-3" strokeWidth={2.6} />}
              {r.ok ? "Looks good" : "Fix 1 thing"}
            </motion.span>
          </motion.li>
        ))}
      </ul>
      <motion.div
        className="mt-3 flex items-center justify-end gap-1 text-[11.5px] font-bold"
        style={{ color: TEAL_DEEP }}
        initial={{ opacity: 0, y: 6 }}
        animate={play ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 0.5, delay: 1.45, ease: EASE_EXPO }}
      >
        3 ready, 1 to fix
        <CheckCheck className="h-4 w-4" strokeWidth={2.6} />
      </motion.div>
    </div>
  );
}

/** Three dots that bounce while someone types. */
function Typing() {
  return (
    <span className="flex items-center gap-1 px-1 py-1">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: BRAND_DARK }}
          animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.14, ease: "easeInOut" }}
        />
      ))}
    </span>
  );
}

/** A question, a moment of typing and the pinned answer. */
function AskVisual({ play }: { play: boolean }) {
  const [answered, setAnswered] = useState(false);
  useEffect(() => {
    if (!play) return;
    const t = window.setTimeout(() => setAnswered(true), 1900);
    return () => window.clearTimeout(t);
  }, [play]);

  return (
    <div className="flex w-full max-w-[290px] flex-col gap-2.5 text-left [transform:translateZ(30px)]" aria-hidden="true">
      <motion.p
        className="self-start rounded-[18px] rounded-bl-md bg-white px-3.5 py-2.5 text-[14px] font-medium leading-snug shadow-[0_1px_2px_rgba(10,15,14,0.06)]"
        style={{ color: INK, transformOrigin: "0% 100%" }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={play ? { opacity: 1, scale: 1 } : undefined}
        transition={{ ...SPRING_POP, delay: 0.3 }}
      >
        How much money do I need to show?
      </motion.p>
      <div className="flex min-h-[64px] justify-end">
        <AnimatePresence mode="popLayout" initial={false}>
          {answered ? (
            <motion.div
              key="answer"
              className="self-end rounded-[18px] rounded-br-md px-3.5 py-2.5 text-[14px] leading-snug"
              style={{ backgroundColor: TEAL, color: BRAND_DARK, transformOrigin: "100% 100%" }}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={SPRING_POP}
            >
              <span className="mb-0.5 block text-[11px] font-bold uppercase tracking-[0.06em] opacity-70">Pinned answer</span>
              <span className="font-semibold">{eur(FEES.FINANCIAL_UNDER_24_ANNUAL)} for one year, if you are under 24.</span>
            </motion.div>
          ) : (
            play && (
              <motion.div
                key="typing"
                className="self-end rounded-[18px] rounded-br-md px-3 py-2"
                style={{ backgroundColor: TEAL, transformOrigin: "100% 100%" }}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                transition={{ ...SPRING_POP, delay: 0.75 }}
              >
                <Typing />
              </motion.div>
            )
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/** The costs, counting up, with ours stamped on last. */
function NumbersVisual({ play }: { play: boolean }) {
  const rows: { k: string; n: number; suffix: string }[] = [
    { k: "Money to show", n: FEES.FINANCIAL_UNDER_24_ANNUAL, suffix: " / year" },
    { k: "Tuition, public", n: FEES.TOTAL_SEMESTER, suffix: " / semester" },
    { k: "Permit application", n: FEES.APPLICATION, suffix: "" },
  ];
  return (
    <div
      className="w-full max-w-[260px] rounded-[20px] bg-white p-4 text-left shadow-[0_1px_2px_rgba(10,15,14,0.06),0_10px_30px_rgba(10,15,14,0.08)] [transform:translateZ(30px)]"
      aria-hidden="true"
    >
      {rows.map((r, i) => (
        <motion.div
          key={r.k}
          className="flex items-baseline justify-between gap-3 border-b py-2 text-[13px]"
          style={{ borderColor: RULE }}
          initial={{ opacity: 0, y: 8 }}
          animate={play ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.5, delay: 0.3 + i * 0.12, ease: EASE_EXPO }}
        >
          <span style={{ color: SECONDARY }}>{r.k}</span>
          <span className="font-bold tabular-nums" style={{ color: INK }}>
            <CountUp value={r.n} format={eur} delay={0.35 + i * 0.12} />
            {r.suffix}
          </span>
        </motion.div>
      ))}
      <motion.div
        className="mt-1 flex items-baseline justify-between gap-3 rounded-lg px-2 py-2 text-[13px]"
        style={{ backgroundColor: TEAL_SOFT }}
        initial={{ opacity: 0, scale: 1.4, rotate: -6 }}
        animate={play ? { opacity: 1, scale: 1, rotate: 0 } : undefined}
        transition={{ ...SPRING_POP, delay: 1.3 }}
      >
        <span className="font-bold" style={{ color: BRAND_DARK }}>
          Migrada
        </span>
        <span className="font-black tabular-nums" style={{ color: BRAND_DARK }}>
          €0
        </span>
      </motion.div>
    </div>
  );
}

const cards = [
  {
    title: "Documents checked",
    body: "Take a photo of your passport, bank statement or admission letter. We read it in seconds and tell you what to fix.",
    Visual: DocsVisual,
    visualBg: TEAL_SOFT,
    fact: "8 document types",
    button: "Check my documents",
    to: "/start",
  },
  {
    title: "A growing community",
    body: "Ask anything and hear from students who made the move and from our team. Every answer stays for the next student.",
    Visual: AskVisual,
    visualBg: PANEL,
    fact: "Usually answered within 48 hours",
    button: "Visit the community",
    to: "/community",
  },
  {
    title: "The real numbers",
    body: "What Austria asks you to show and what you pay, each figure from an official source.",
    Visual: NumbersVisual,
    visualBg: TEAL_SOFT,
    fact: "Checked for 2026",
    button: "See all costs",
    to: "/blog/austria-student-visa-financial-requirements-2026",
  },
];

function Card({ card, index }: { card: (typeof cards)[number]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const play = useInView(ref, { once: true, margin: "-120px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50, rotate: index === 1 ? 0 : index === 0 ? -2 : 2 }}
      animate={play ? { opacity: 1, y: 0, rotate: 0 } : undefined}
      transition={{ duration: 0.9, delay: index * 0.12, ease: EASE_EXPO }}
    >
      <TiltCard className="h-full bg-white" style={{ borderRadius: 32 }}>
        <article className="flex h-full flex-col items-center p-5 pb-8 text-center sm:p-6 sm:pb-9">
          <div
            className="flex h-[210px] w-full items-center justify-center px-4 [transform-style:preserve-3d]"
            style={{ backgroundColor: card.visualBg, borderRadius: 24 }}
          >
            <card.Visual play={play} />
          </div>
          <h3
            className="mt-7 px-2 font-extrabold leading-tight tracking-[-0.025em]"
            style={{ fontSize: "clamp(1.5rem, 1.2rem + 0.8vw, 1.875rem)", color: INK }}
          >
            {card.title}
          </h3>
          <p className="mt-3 max-w-[22rem] px-2 text-[16px] leading-relaxed sm:text-[17px]" style={{ color: SECONDARY }}>
            {card.body}
          </p>
          <p className="mt-5 inline-flex items-center gap-2.5 text-[16px] font-bold sm:text-[17px]" style={{ color: BRAND_DARK }}>
            <CircleCheck className="h-6 w-6 shrink-0" style={{ color: TEAL_DEEP }} aria-hidden="true" />
            <span className="underline decoration-[#2EE6D6] decoration-2 underline-offset-[6px]">{card.fact}</span>
          </p>
          {/* Pinned to the bottom, so the three buttons line up like Wise's. */}
          <div className="mt-auto pt-7">
            <Link
              to={card.to}
              className="inline-flex items-center justify-center rounded-full border-2 px-7 py-3 text-[16px] font-bold transition-colors hover:bg-[#F2F2ED]"
              style={{ borderColor: BRAND_DARK, color: BRAND_DARK }}
            >
              {card.button}
            </Link>
          </div>
        </article>
      </TiltCard>
    </motion.div>
  );
}

/**
 * Section 2, straight after the hero: Wise's "Do more with" block. One
 * centred headline, one button, three cards that show exactly what a student
 * gets: document checks, a community and honest numbers. Each card ends like
 * Wise's: one checked fact, then its own button. Each card plays a few seconds
 * of the real thing when it arrives and tilts under the cursor.
 *
 * Partners (insurance, bank, housing) are deliberately absent. They are how
 * Migrada will earn, not why a student comes.
 */
export function MoreThanAVisa() {
  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: PANEL }}>
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <KineticHeading
            text="Do more with Migrada"
            className="font-black leading-[0.95] tracking-[-0.04em] [text-wrap:balance]"
            style={{ fontSize: "var(--text-h2)", color: INK }}
          />
          <motion.p
            className="mx-auto mt-5 max-w-2xl text-[18px] leading-relaxed sm:text-[20px]"
            style={{ color: SECONDARY }}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE_EXPO }}
          >
            Migrada is more than a visa plan. Here is what you can use, from your first question to your first week in Austria.
          </motion.p>
          <motion.div
            className="mt-8"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE_EXPO }}
          >
            <Magnetic>
              <Link
                to="/start"
                className="inline-flex items-center justify-center rounded-full px-9 py-4 text-[18px] font-bold transition-[filter,transform] hover:brightness-95 active:scale-95"
                style={{ backgroundColor: TEAL, color: BRAND_DARK }}
              >
                Get visa help
              </Link>
            </Magnetic>
          </motion.div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:mt-16 md:grid-cols-3 md:gap-6">
          {cards.map((card, i) => (
            <Card key={card.title} card={card} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
